/*
 * -----------------------------------------------------------------------
 *  <copyright file="data-review-grid.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { orderBy } from '@progress/kendo-data-query';
import React, { useEffect, useState } from 'react';

import { useGridCells, useTranslation } from 'hooks';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import ExpandableGrid from 'views/components/core/expandable/grid';
import { GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';

import './data-review-grid.scss';

const DataReviewGrid = ({ items, actions }: any) => {
    const { t } = useTranslation();
    const { MaxLengthCell, YesNoCell, DateCell } = useGridCells();

    const [data, setData] = useState([]);

    useEffect(() => {
        let array = [...items];
        const newData = restructuredUpdatedCreatedBy(array);
        array = orderBy(newData, [{ field: 'name', dir: 'asc' }]) ?? [];

        setData(array);
    }, [items]);

    return (
        <ExpandableGrid
            customKey="data-review-grid"
            id="dataReviewGrid"
            key="dataReviewGrid"
            items={data}
            rowActions={actions}
            groupable={false}
            showPager
            addScroll
            scrollable="scrollable"
            reorderable
            pageable={false}
            resizable
            options={{
                allowExcelExport: false,
                allowExpandCollapseAll: false,
                useFilterChips: false,
            }}
            detail={null}
            columns={[
                {
                    field: 'name',
                    label: t('name'),
                    id: 'data-review-name',
                    minWidth: 140,
                    lockedByDefault: true,
                    cells: { data: MaxLengthCell(30) },
                },
                {
                    field: '_baseData',
                    id: 'data-review-base-data',
                    label: t('admin:baseData'),
                    minWidth: 240,
                    cells: { data: MaxLengthCell(70) },
                },
                {
                    field: '_columnsNum',
                    id: 'data-review-columns-num',
                    label: t('admin:num_columns'),
                    minWidth: 220,
                    filter: 'numeric',
                },
                {
                    field: 'description',
                    id: 'data-review-description',
                    label: t('description'),
                    minWidth: 200,
                    cells: { data: MaxLengthCell(150) },
                },
                {
                    field: 'active',
                    id: 'data-review-active',
                    label: t('active'),
                    minWidth: 140,
                    cells: { data: YesNoCell },
                    filterUi: GridColumnMenuActiveFilter,
                },
                {
                    label: t('ra:createdBy'),
                    field: '_createdByString',
                    id: 'data-review-createdby',
                    minWidth: 180,
                },
                {
                    label: t('ra:createdDate'),
                    field: 'created',
                    id: 'data-review-created',
                    filter: 'date',
                    minWidth: 180,
                    cells: { data: DateCell },
                },
                {
                    label: t('ra:updatedBy'),
                    field: '_updatedByString',
                    id: 'data-review-updatedby',
                    minWidth: 180,
                },
                {
                    label: t('ra:updatedDate'),
                    field: 'updated',
                    id: 'data-review-updated',
                    filter: 'date',
                    minWidth: 190,
                    cells: { data: DateCell },
                },
                {
                    field: 'recordId',
                    label: 'ID',
                    minWidth: 120,
                    id: 'action-id',
                    filter: 'numeric',
                    showByDefault: false,
                },
            ]}
        />
    );
};

export default DataReviewGrid;
